import { Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import { Eye, EyeOff } from 'lucide-react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Roi } from '~/domain/dicom/dicomstructureset';
import useRoi from '~/hooks/structure-set/useRoi';
import useViewManagerEvents from '~/hooks/useViewManagerEvents';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { reviewToolState } from '~/state/atoms/toolsAtoms';
import StructureColorPicker from './ColorPicker';
import StructureReviewStatus from './StructureReviewStatus';
import StructureField from './StructureField';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import useStructureSet from '~/hooks/structure-set/useStructureSet';

export default function StructureProperties({ roi }: { roi: Roi }) {
    const {
        roi: roiState,
        setVisible,
        setName,
        setColor,
        structureCanBeEdited,
    } = useRoi(roi);
    const { setStructureSetVisibility } = useStructureSet();
    const { focusOnStructure } = useViewManagerEvents();
    const [selectedROI, setSelectedROI] =
        useRecoilState<number>(selectedROIState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const reviewModeOpen = useRecoilValue(reviewToolState);

    const ROINumber = roi.roiNumber;
    const isSelected = selectedROI === ROINumber;

    const handleToggleVisibility = (
        _: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        setVisible(checked);
        const areAllRoisVisible =
            currentStructureSet?.getRoiByVisibility(true).length ===
            currentStructureSet?.numberOfStructures;
        const areAllRoisInvisible =
            currentStructureSet?.getRoiByVisibility(false).length ===
            currentStructureSet?.numberOfStructures;
        if (areAllRoisVisible) {
            setStructureSetVisibility(true);
        }
        if (areAllRoisInvisible) {
            setStructureSetVisibility(false);
        }
    };

    const handleSelectROI = () => {
        setSelectedROI(ROINumber);
        if (reviewModeOpen) {
            focusOnStructure(ROINumber);
        }
    };

    return (
        roiState && (
            <>
                <Checkbox
                    onChange={handleToggleVisibility}
                    checked={roiState?.visible || isSelected}
                    inputProps={{ 'aria-label': 'Checkbox demo' }}
                    icon={<EyeOff size={16} />}
                    checkedIcon={<Eye size={16} />}
                    size="small"
                    sx={{
                        '&': {
                            padding: 0.5,
                        },
                    }}
                    disabled={isSelected}
                />
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    width="100%"
                    onClick={handleSelectROI}
                    data-testid="structure-properties-field"
                >
                    <StructureColorPicker roi={roiState} onChange={setColor} />
                    <StructureField
                        roi={roiState}
                        onNameChange={setName}
                        isEditable={structureCanBeEdited}
                    />
                    <StructureReviewStatus status={roiState?.approvalStatus} />
                </Box>
            </>
        )
    );
}
